// opticodds.ts
// Minimal OpticOdds API v3 client (common, squads, fixtures, odds, results)
// Node 18+ (global fetch) or pass your own fetch.
//
// Usage:
//   import { OpticOddsClient } from "./opticodds";
//   const oo = new OpticOddsClient({ apiKey: process.env.OPTICODDS_KEY! });
//   const leagues = await oo.common.leagues({ sport: "basketball" });

// ============================================================================
// OpticOdds API v3 TypeScript Type Definitions
// Based on official documentation at https://developer.opticodds.com
// ============================================================================

// ============================================================================
// Query Parameter Types
// ============================================================================

// Base query params type (for internal use by the client)
export type QueryValue = string | number | boolean | Date | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

export interface SportsParams extends Record<string, QueryValue | QueryValue[]> {
	// No specific required params
}

export interface LeaguesParams extends Record<string, QueryValue | QueryValue[]> {
	sport?: string;
}

export interface SportsbooksParams extends Record<string, QueryValue | QueryValue[]> {
	// No specific required params
}

export interface MarketsParams extends Record<string, QueryValue | QueryValue[]> {
	sport?: string; // Filter markets by sport
}

export interface MarketsActiveParams extends Record<string, QueryValue | QueryValue[]> {
	sportsbook?: string | string[]; // Filter by sportsbook(s)
	fixture_id?: string | string[]; // Filter by fixture ID(s)
}

export interface MarketsSettleableParams extends Record<string, QueryValue | QueryValue[]> {
	league?: string; // Filter by league
	sport?: string; // Filter by sport
}

export interface TeamsParams extends Record<string, QueryValue | QueryValue[]> {
	sport?: string;
	league?: string;
	id?: string | string[];
	include_statsperform_id?: boolean;
}

export interface PlayersParams extends Record<string, QueryValue | QueryValue[]> {
	sport?: string;
	league?: string;
	id?: string | string[];
	numerical_id?: string | string[];
	base_id?: string | string[];
	team_id?: string | string[];
	team_base_id?: string | string[];
	include_statsperform_id?: boolean;
	page?: number;
}

export interface FixturesParams extends Record<string, QueryValue | QueryValue[]> {
	sport?: string | string[];
	league?: string | string[];
	id?: string | string[];
	team_id?: string;
	start_date_after?: string | Date;
	start_date_before?: string | Date;
	status?: FixtureStatus | FixtureStatus[];
	include_starting_lineups?: boolean;
	include_statsperform_id?: boolean;
	page?: number;
	per_page?: number;
}

export interface FixturesActiveParams extends FixturesParams {
	// Same as FixturesParams
}

export interface FixturesOddsParams extends Record<string, QueryValue | QueryValue[]> {
	// At least one of these is required
	fixture_id?: string | string[]; // Up to 5 per request
	team_id?: string | string[];
	player_id?: string | string[];

	// Required: 1-5 sportsbooks
	sportsbook: string | string[];

	// Optional filters
	market?: string | string[]; // Can pass id or name
	is_main?: boolean; // Filter for "main" or "given" lines
	odds_format?: OddsFormat; // Defaults to AMERICAN

	// Additional optional parameters
	sport?: string;
	league?: string;
	start_date_after?: string | Date;
	start_date_before?: string | Date;
	include_starting_lineups?: boolean;
	include_statsperform_id?: boolean;
}

export interface FixturesOddsHistoricalParams extends Record<string, QueryValue | QueryValue[]> {
	fixture_id: string | string[];
	sportsbook?: string | string[];
	market?: string | string[];
	is_main?: boolean;
	odds_format?: OddsFormat;
}

export interface FixturesResultsParams extends Record<string, QueryValue | QueryValue[]> {
	sport?: string;
	league?: string;
	fixture_id?: string;
	team_id?: string;
	start_date_after?: string | Date;
	start_date_before?: string | Date;
}

export interface PlayerResultsParams extends Record<string, QueryValue | QueryValue[]> {
	sport?: string;
	league?: string;
	fixture_id?: string;
	player_id?: string | string[];
	team_id?: string;
}

export interface PlayerResultsLastXParams extends Record<string, QueryValue | QueryValue[]> {
	player_id?: string | string[];
	team_id?: string;
	season_year?: string;
	earliest_date?: string;
	lookback_num?: string | number;
}

// ============================================================================
// Common Types
// ============================================================================

export interface SourceIds {
	statsperform_id?: string;
	[key: string]: string | undefined;
}

// ============================================================================
// Sports
// ============================================================================

export interface Sport {
	id: string;
	name: string;
	numerical_id?: number;
	main_markets?: Market[];
}

// ============================================================================
// Leagues
// ============================================================================

export interface League {
	id: string;
	name: string;
	numerical_id?: number;
	sport?: Sport;
	region?: string;
	region_code?: string;
	gender?: "men" | "women";
}

// ============================================================================
// Markets
// ============================================================================

export interface Market {
	id: string;
	name: string;
	numerical_id: number;
	sports?: MarketSport[];
}

export interface MarketSport {
	id: string;
	name: string;
	leagues: MarketLeague[];
}

export interface MarketLeague {
	id: string;
	name: string;
	sportsbooks: MarketSportsbook[];
}

export interface MarketSportsbook {
	id: string;
	name: string;
}

// ============================================================================
// Sportsbooks
// ============================================================================

export interface Sportsbook {
	id: string;
	name: string;
	logo: string;
	is_onshore: boolean;
	is_active: boolean;
}

export interface SportsbookLastPolled {
	sportsbook: string;
	last_polled: number;
}

// ============================================================================
// Teams
// ============================================================================

export interface Team {
	id: string;
	name: string;
	numerical_id: number | null;
	is_active: boolean;
	city: string;
	mascot: string;
	nickname: string;
	abbreviation: string;
	division: string;
	conference: string;
	base_id: number;
	logo: string;
	source_ids: SourceIds;
	sport: Sport;
	league: League;
}

export interface Competitor {
	id: string;
	name: string;
	numerical_id?: number | null;
	abbreviation: string;
	logo: string;
}

// ============================================================================
// Players
// ============================================================================

export interface Player {
	id: string;
	name: string;
	position?: string;
	number?: number;
	numerical_id?: number | null;
	is_active?: boolean;
	first_name?: string;
	last_name?: string;
	age?: number;
	height?: number;
	weight?: number;
	experience?: string | null;
	logo?: string;
	base_id?: number;
	source_ids?: Record<string, string>;
	sport?: Sport;
	league?: League;
	team?: {
		id: string;
		name: string;
		numerical_id?: number;
	};
	// Legacy fields (kept for backwards compatibility)
	player_id?: string;
	player_name?: string;
	player_team?: string;
	player_position?: string;
	player_batting_throwing?: string | null;
	team_id?: string;
	is_substitute?: boolean;
}

// ============================================================================
// Fixtures
// ============================================================================

export type FixtureStatus = "live" | "half" | "unplayed" | "completed" | "cancelled" | "suspended" | "delayed";

export interface Fixture {
	id: string;
	numerical_id?: number;
	game_id: string;
	start_date: string; // ISO 8601 datetime
	home_competitors: Competitor[];
	away_competitors: Competitor[];
	home_team_display: string;
	away_team_display: string;
	status: FixtureStatus;
	is_live: boolean;
	sport: Sport;
	league: League;
	home_starter?: string | null;
	home_record?: string | null;
	home_seed?: string | null;
	home_rotation_number?: number | null;
	away_starter?: string | null;
	away_record?: string | null;
	away_seed?: string | null;
	away_rotation_number?: number | null;
	tournament?: string | null;
	tournament_stage?: string | null;
	has_odds?: boolean;
	venue_name?: string | null;
	venue_location?: string | null;
	venue_neutral?: boolean;
	broadcast?: string;
	result?: FixtureResultData | null;
	lineups?: LineupData;
	season_type?: string;
	season_year?: string;
	season_week?: string | null;
	weather?: string | null;
	weather_temp?: string | null;
	source_ids?: SourceIds;
	odds?: Odd[];
}

export interface LineupData {
	home: Player[];
	away: Player[];
}

// ============================================================================
// Odds
// ============================================================================

export type OddsFormat = "AMERICAN" | "DECIMAL" | "PROBABILITY" | "MALAY" | "HONG_KONG" | "INDONESIAN";

export type SelectionLine = "over" | "under" | null;

export interface Odd {
	id: string;
	sportsbook: string;
	market: string;
	name: string;
	is_main: boolean;
	selection: string;
	normalized_selection: string;
	market_id: string;
	selection_line: SelectionLine;
	player_id: string | null;
	team_id: string | null;
	price: number;
	timestamp: number;
	grouping_key: string;
	points: number | null;
	deep_link: DeepLink | null;
	limits: Limits | null;
}

export interface DeepLink {
	ios: string;
	android: string;
	desktop: string;
}

export interface Limits {
	max: number;
}

// ============================================================================
// Results
// ============================================================================

export interface FixtureResultData {
	scores: ScoreData;
	in_play_data?: InPlayData;
}

export interface ScoreData {
	home: TeamScore;
	away: TeamScore;
}

export interface TeamScore {
	total: number;
	periods: Record<string, number>;
	aggregate: number | null;
}

export interface InPlayData {
	period: string;
	clock: string | null;
	last_play: string | null;
	time_min?: number | null;
	time_sec?: number | null;
	balls?: number;
	outs?: number;
	strikes?: number;
	runners?: Runners;
	batter?: Player | null;
	pitcher?: Player | null;
	possession?: string | null;
	down?: number | null;
	distance_to_go?: number | null;
	field_position?: string | null;
}

export interface Runners {
	third: Player | null;
	second: Player | null;
	first: Player | null;
}

export interface FixtureResult {
	sport: Sport;
	league: League;
	fixture: Fixture;
	scores: ScoreData;
	in_play: InPlayData;
	events: Event[];
	stats: TeamStats;
	extra: ExtraInfo;
	retirement_info: RetirementInfo | null;
}

export interface TeamStats {
	home: StatsPeriod[];
	away: StatsPeriod[];
}

export interface StatsPeriod {
	period: string;
	stats: BaseballStats | Record<string, unknown>;
}

export interface BaseballStats {
	rbi: number;
	hits: number;
	runs: number;
	at_bats: number;
	total_bases: number;
	double_plays: number;
	triple_plays: number;
	batting_walks: number;
	batting_strikeouts: number;
	runners_left_on_base: number;
	scoring_position_successes: number;
	scoring_position_opportunities: number;
}

export interface ExtraInfo {
	decision: string | null;
	decision_method: string | null;
}

export interface Event {
	[key: string]: unknown;
}

export type RetirementInfo = null | {
	[key: string]: unknown;
};

// ============================================================================
// Player Results
// ============================================================================

export interface PlayerResultStats {
	period: string;
	stats: Record<string, number>;
}

export interface PlayerResultItem {
	player: {
		id: string;
		name: string;
		position?: string;
		number?: number;
	};
	team: {
		id: string;
		name: string;
	};
	status?: string;
	stats: PlayerResultStats[];
	market_stats: Record<string, number>
}

export interface FixturePlayerResult {
	sport: Sport;
	league: League;
	fixture: Fixture;
	results: PlayerResultItem[];
}

/** @deprecated Use FixturePlayerResult instead */
export interface PlayerResult {
	player_id: string;
	player_name: string;
	team_id: string;
	fixture_id: string;
	stats: Record<string, unknown>;
}

// ============================================================================
// Client Types
// ============================================================================

export type Primitive = string | number | boolean;

export type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

export interface OpticOddsClientOptions {
  apiKey: string;

  /** Default: https://api.opticodds.com/api/v3 */
  baseUrl?: string;

  /** Default: 60_000 */
  timeoutMs?: number;

  /** Default: 2 */
  retries?: number;

  /** Default: header auth (X-Api-Key). If true, uses ?key=... instead */
  keyInQuery?: boolean;

  /** Default: globalThis.fetch */
  fetch?: FetchLike;

  /** Extra headers (e.g. User-Agent) */
  headers?: Record<string, string>;
}

export interface ApiEnvelope<T> {
  data: T;
  page?: number;
  total_pages?: number;
  [k: string]: unknown;
}

export class OpticOddsError extends Error {
  readonly name = "OpticOddsError";
  constructor(
    message: string,
    public readonly status: number,
    public readonly url: string,
    public readonly body?: unknown
  ) {
    super(message);
  }
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const pairs: Array<[string, string]> = [];

  for (const [k, v] of Object.entries(params)) {
    const arr = Array.isArray(v) ? v : [v];
    for (const item of arr) {
      if (item === null || item === undefined) continue;
      const value = item instanceof Date ? item.toISOString() : String(item);
      pairs.push([k, value]);
    }
  }

  if (!pairs.length) return "";
  return (
    "?" +
    pairs
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join("&")
  );
}

function safeJsonParse(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

class HttpClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;
  private readonly retries: number;
  private readonly fetchImpl: FetchLike;
  private readonly apiKey: string;
  private readonly keyInQuery: boolean;
  private readonly headers: Record<string, string>;

  constructor(opts: OpticOddsClientOptions) {
    this.baseUrl = (opts.baseUrl ?? "https://api.opticodds.com/api/v3").replace(/\/+$/, "");
    this.timeoutMs = opts.timeoutMs ?? 60_000;
    this.retries = opts.retries ?? 2;
    this.fetchImpl = opts.fetch ?? globalThis.fetch;
    if (!this.fetchImpl) {
      throw new Error("No fetch implementation found. Use Node 18+ or pass opts.fetch.");
    }
    this.apiKey = opts.apiKey;
    this.keyInQuery = !!opts.keyInQuery;
    this.headers = opts.headers ?? {};
  }

  get<T>(path: string, params?: QueryParams, init?: RequestInit) {
    const url = this.makeUrl(path, params);
    return this.request<ApiEnvelope<T>>(url, { method: "GET", ...init });
  }

  private makeUrl(path: string, params?: QueryParams) {
    const p: QueryParams = { ...(params ?? {}) };
    if (this.keyInQuery) p.key = this.apiKey;

    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `${this.baseUrl}${cleanPath}${buildQuery(p)}`;
  }

  private async request<T>(url: string, init: RequestInit): Promise<T> {
    const headers: Record<string, string> = {
      ...(init.headers as Record<string, string> | undefined),
      ...this.headers
    };
    if (!this.keyInQuery) headers["X-Api-Key"] = this.apiKey;

    const doOnce = async (): Promise<T> => {
      const ac = new AbortController();
      let timeout: ReturnType<typeof setTimeout> | null = null;
      try {
        const timeout_promise = new Promise<never>((_, reject) => {
          timeout = setTimeout(() => {
            ac.abort();
            reject(new OpticOddsError(
              `Request timeout after ${this.timeoutMs}ms for ${url}`,
              408,
              url,
            ));
          }, this.timeoutMs);
        });

        const response_promise = (async () => {
          const res = await this.fetchImpl(url, { ...init, headers, signal: ac.signal });
          const text = await res.text();
          const body =
            text && (text.trim().startsWith("{") || text.trim().startsWith("["))
              ? safeJsonParse(text)
              : text;

          if (!res.ok) {
            throw new OpticOddsError(`HTTP ${res.status} for ${url}`, res.status, url, body);
          }

          return (body as T) ?? ({} as T);
        })();

        return await Promise.race([response_promise, timeout_promise]);
      } finally {
        if (timeout) {
          clearTimeout(timeout);
        }
      }
    };

    let lastErr: unknown;
    for (let attempt = 0; attempt <= this.retries; attempt++) {
      try {
        return await doOnce();
      } catch (e: any) {
        lastErr = e;
        const status = e?.status as number | undefined;
        const retryable = status === 429 || (typeof status === "number" && status >= 500);
        if (attempt < this.retries && retryable) {
          const backoff = 250 * Math.pow(2, attempt) + Math.floor(Math.random() * 100);
          await sleep(backoff);
          continue;
        }
        throw e;
      }
    }
    throw lastErr;
  }
}

// ----------------- Resources -----------------

class CommonResource {
  constructor(private readonly http: HttpClient) {}

  sports(params?: SportsParams) { return this.http.get<Sport[]>("/sports", params); }
  sportsActive(params?: SportsParams) { return this.http.get<Sport[]>("/sports/active", params); }

  leagues(params?: LeaguesParams) { return this.http.get<League[]>("/leagues", params); }
  leaguesActive(params?: LeaguesParams) { return this.http.get<League[]>("/leagues/active", params); }

  sportsbooks(params?: SportsbooksParams) { return this.http.get<Sportsbook[]>("/sportsbooks", params); }
  sportsbooksActive(params?: SportsbooksParams) { return this.http.get<Sportsbook[]>("/sportsbooks/active", params); }
  sportsbooksLastPolled(params?: SportsbooksParams) { return this.http.get<SportsbookLastPolled[]>("/sportsbooks/last-polled", params); }

  markets(params?: MarketsParams) { return this.http.get<Market[]>("/markets", params); }
  marketsActive(params?: MarketsActiveParams) { return this.http.get<Market[]>("/markets/active", params); }
  marketsSettleable(params?: MarketsSettleableParams) { return this.http.get<Market[]>("/markets/settleable", params); }

  marketTypes(params?: MarketsParams) { return this.http.get<Market[]>("/market-types", params); }
}

class SquadsResource {
  constructor(private readonly http: HttpClient) {}

  /** Must pass sport|league|id (per docs) */
  teams(params: TeamsParams) { return this.http.get<Team[]>("/teams", params); }

  /** Must pass sport|league|id (per docs) */
  players(params: PlayersParams) { return this.http.get<Player[]>("/players", params); }
}

class FixturesResource {
  constructor(private readonly http: HttpClient) {}

  /** Must pass at least one of sport|league|id|team_id (per docs) */
  fixtures(params: FixturesParams) { return this.http.get<Fixture[]>("/fixtures", params); }

  /** Same params as /fixtures, but active fixtures that have/had odds */
  active(params: FixturesActiveParams) { return this.http.get<Fixture[]>("/fixtures/active", params); }
}

class OddsResource {
  constructor(private readonly http: HttpClient) {}

  /**
   * /fixtures/odds
   * Pass sportsbook as array => repeated query params (sportsbook=A&sportsbook=B)
   * Returns fixtures with odds data
   */
  fixturesOdds(params: FixturesOddsParams) { return this.http.get<Fixture[]>("/fixtures/odds", params); }

  /**
   * /fixtures/odds/historical
   * Requires single fixture_id; sportsbook can be 1..5 (per docs)
   * Returns historical odds data
   */
  fixturesOddsHistorical(params: FixturesOddsHistoricalParams) {
    return this.http.get<Odd[]>("/fixtures/odds/historical", params);
  }
}

class ResultsResource {
  constructor(private readonly http: HttpClient) {}

  fixturesResults(params?: FixturesResultsParams) {
    return this.http.get<FixtureResult[]>("/fixtures/results", params);
  }

  fixturesPlayerResults(params?: PlayerResultsParams) {
    return this.http.get<FixturePlayerResult[]>("/fixtures/player-results", params);
  }

  fixturesPlayerResultsLastX(params?: PlayerResultsLastXParams) {
    return this.http.get<FixturePlayerResult[]>("/fixtures/player-results/last-x", params);
  }

  fixturesResultsHeadToHead(params?: FixturesResultsParams) {
    return this.http.get<FixtureResult[]>("/fixtures/results/head-to-head", params);
  }
}

// ----------------- Client -----------------

export class OpticOddsClient {
  private readonly http: HttpClient;

  readonly common: CommonResource;
  readonly squads: SquadsResource;
  readonly fixtures: FixturesResource;
  readonly odds: OddsResource;
  readonly results: ResultsResource;

  constructor(opts: OpticOddsClientOptions) {
    this.http = new HttpClient(opts);
    this.common = new CommonResource(this.http);
    this.squads = new SquadsResource(this.http);
    this.fixtures = new FixturesResource(this.http);
    this.odds = new OddsResource(this.http);
    this.results = new ResultsResource(this.http);
  }
}

export function createOpticOddsClient(opts: OpticOddsClientOptions) {
	return new OpticOddsClient(opts);
}
